import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { 
  ArrowRight, Rocket, CheckCircle, Zap, Brain, Cpu, BarChart3
} from "lucide-react";
import { SEO } from "@/components/shared";
import { PHOTOS, PhotoBand, CardImage, FramedPhoto } from "@/components/Photo";

const DIGITAL_IMAGES = [PHOTOS.genai, PHOTOS.code, PHOTOS.cables];
import { MEDIA, fadeInUp, staggerContainer, viewportOnce, HERO_IMAGES } from "@/constants";

const GTM_SERVICES = [
  { id: 'gtm-units', title: "New Business Units", desc: "Strategic planning and execution support for establishing new business verticals" },
  { id: 'gtm-teams', title: "Team Expansions", desc: "Talent acquisition strategy and organizational design for scaling teams" },
  { id: 'gtm-reskill', title: "Reskilling & Strategic Consulting", desc: "Workforce transformation programs aligned with business objectives" },
  { id: 'gtm-launches', title: "New Product Launches", desc: "End-to-end launch strategy from market research to go-to-market execution" },
  { id: 'gtm-research', title: "Primary & Secondary Research", desc: "Comprehensive market intelligence and competitive analysis" },
  { id: 'gtm-market', title: "Market Assessments", desc: "In-depth market sizing, opportunity identification, and entry strategies" },
  { id: 'gtm-competitor', title: "Competitor Assessments", desc: "Detailed competitive landscape analysis and positioning strategies" },
];

const DIGITAL_SERVICES = [
  { id: 'dig-ai', icon: Brain, title: "AI-Driven Automation", desc: "Leverage artificial intelligence to automate repetitive tasks, enhance decision-making, and boost operational efficiency" },
  { id: 'dig-app', icon: Cpu, title: "Custom App Development", desc: "Tailored software solutions designed to address your unique business challenges and digital requirements" },
  { id: 'dig-infra', icon: BarChart3, title: "IT Infrastructure", desc: "Modern, scalable infrastructure solutions including cloud migration, cybersecurity, and system optimization" },
];

export default function ServicesPage() {
  return (
    <div className="pt-24">
      <SEO 
        title="Services"
        description="Explore Qbic Catalyst Partners consulting services: Go-To-Market Strategy for Enterprises and Digital Transformation for MSMEs. Strategic consulting, market research, AI automation, and IT infrastructure."
        keywords="go-to-market consulting, digital transformation, enterprise consulting, MSME consulting, market research, competitor analysis, AI automation, IT infrastructure"
        path="/services"
      />
      {/* Hero */}
      <section className="section page-hero" style={{ "--hero-img": `url(${HERO_IMAGES.services})` }}>
        <div className="container-custom">
          <motion.div
            initial="initial"
            animate="animate"
            variants={staggerContainer}
            className="max-w-4xl"
          >
            <motion.p variants={fadeInUp} className="text-[#C9A962] text-sm uppercase tracking-[0.2em] mb-4">
              Our Services
            </motion.p>
            <motion.h1 variants={fadeInUp} className="text-4xl md:text-5xl font-bold mb-6">
              Strategic Consulting for{' '}
              <span className="text-gradient">Enterprise Growth</span>
            </motion.h1>
            <motion.p variants={fadeInUp} className="text-xl text-[#A0ABC0]">
              Comprehensive consulting solutions designed to accelerate your business transformation and market success.
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Go-To-Market Consulting */}
      <section className="section theme-light tint-ivory">
        <div className="container-custom">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={viewportOnce}
            variants={staggerContainer}
          >
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
              <motion.div variants={fadeInUp}>
                <div className="flex items-start gap-4 mb-6">
                  <div className="icon-container flex-shrink-0">
                    <Rocket className="text-[#C9A962]" size={28} />
                  </div>
                  <div>
                    <h2 className="text-3xl md:text-4xl font-bold mb-2">Go-To-Market Consulting</h2>
                    <p className="text-lg text-[#C9A962] font-semibold border-b-2 border-[#C9A962] inline-block pb-1">For Enterprise</p>
                  </div>
                </div>
                <p className="text-[#A0ABC0] text-lg">
                  Partner with our seasoned consultants to navigate complex market dynamics, accelerate growth initiatives, and establish competitive advantages. Our enterprise-focused approach combines strategic insight with practical execution.
                </p>
              </motion.div>
              <motion.div variants={fadeInUp} className="flex justify-center">
                <img 
                  src={MEDIA.gtmConsulting} 
                  alt="Go-To-Market Strategy Session"
                  className="rounded-lg shadow-2xl w-full max-w-lg object-cover"
                />
              </motion.div>
            </div>

            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-2 lg:grid-cols-3 gap-6"
            >
              {GTM_SERVICES.map((service) => (
                <motion.div
                  key={service.id}
                  variants={fadeInUp}
                  className="p-6 bg-[#0A1628] rounded-lg border border-[#2A3648] card-hover flex flex-col h-full"
                >
                  <CheckCircle className="text-[#C9A962] mb-4" size={20} />
                  <h4 className="font-bold mb-2">{service.title}</h4>
                  <p className="text-[#A0ABC0] text-sm">{service.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      <PhotoBand
        src={PHOTOS.boardroom}
        eyebrow="How We Work"
        title="From strategy on paper to results in the market"
        text="Seasoned consultants who stay with you from research and planning through launch and scale."
        testId="services-band"
      />

      {/* Digital Transformation */}
      <section className="section bg-[#0A1628]">
        <div className="container-custom">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={viewportOnce}
            variants={staggerContainer}
          >
            <div className="grid lg:grid-cols-2 gap-12 items-center mb-12">
              <motion.div variants={fadeInUp} className="order-2 lg:order-1 flex justify-center">
                <img 
                  src={MEDIA.digitalTransform} 
                  alt="Digital Transformation Infrastructure"
                  className="rounded-lg shadow-2xl w-full max-w-lg object-cover aspect-video"
                />
              </motion.div>
              <motion.div variants={fadeInUp} className="order-1 lg:order-2">
                <div className="flex items-start gap-4 mb-6">
                  <div className="icon-container flex-shrink-0">
                    <Zap className="text-[#C9A962]" size={28} />
                  </div>
                  <div>
                    <h2 className="text-3xl md:text-4xl font-bold mb-2">Digital Transformation Consulting</h2>
                    <p className="text-lg text-[#C9A962] font-semibold border-b-2 border-[#C9A962] inline-block pb-1">For MSMEs</p>
                  </div>
                </div>
                <p className="text-[#A0ABC0] text-lg">
                  Empower your small or medium enterprise with cutting-edge digital solutions. We make advanced technology accessible and practical, helping you compete effectively in the digital economy without overwhelming complexity.
                </p>
              </motion.div>
            </div>

            <motion.div
              variants={staggerContainer}
              className="grid md:grid-cols-3 gap-8"
            >
              {DIGITAL_SERVICES.map((service, i) => (
                <motion.div
                  key={service.id}
                  variants={fadeInUp}
                  className="group overflow-hidden p-8 bg-[#121E30] rounded-lg border border-[#2A3648] card-hover flex flex-col h-full"
                >
                  <CardImage src={DIGITAL_IMAGES[i]} alt={service.title} />
                  <div className="icon-container mb-6">
                    <service.icon className="text-[#C9A962]" size={28} />
                  </div>
                  <h3 className="text-xl font-bold mb-4">{service.title}</h3>
                  <p className="text-[#A0ABC0]">{service.desc}</p>
                </motion.div>
              ))}
            </motion.div>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="section theme-light tint-sky">
        <div className="container-custom">
          <motion.div
            initial="initial"
            whileInView="animate"
            viewport={viewportOnce}
            variants={staggerContainer}
            className="grid lg:grid-cols-2 gap-16 items-center"
          >
            <FramedPhoto src={PHOTOS.msme} alt="Business owner reviewing growth on a tablet" badge={{ value: "13+", label: "Enterprise & MSME clients" }} testId="services-cta-photo" />
            <div>
            <motion.h2 variants={fadeInUp} className="text-3xl md:text-4xl font-bold mb-6">
              Ready to Accelerate Your Growth?
            </motion.h2>
            <motion.p variants={fadeInUp} className="text-[#A0ABC0] text-lg mb-8">
              Let's discuss how our consulting services can address your unique challenges and unlock new opportunities.
            </motion.p>
            <motion.div variants={fadeInUp}>
              <Link to="/contact" className="btn-primary inline-flex items-center gap-2" data-testid="services-cta">
                Schedule a Discovery Call <ArrowRight size={18} />
              </Link>
            </motion.div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
